import { DeliveryOrder } from '@core/models/delivery/delivery-order';
import { DeliveryOrderStatus } from '@core/models/delivery/delivery-order-status';
import { DeliveryOrderMarkerType } from '@core/models/delivery/map/delivery-order-marker-type';
import { DeliveryOrderQuoteManager } from '@core/models/delivery/quote/delivery-order-quote-manager';
import { DeliveryOrderQuoteManagerStatus } from '@core/models/delivery/quote/delivery-order-quote-manager-status';
import { MapMarker } from '@core/models/map/map-marker';
import { environment } from '@environments/environment';
import * as moment from 'moment';
import { MOMENT_UNIT } from './moment';

export function orderManagerSortingByTimecardStatus(
    managerA: DeliveryOrderQuoteManager,
    managerB: DeliveryOrderQuoteManager
) {
    let orderSortingOrdering = {};
    const sortingOrder: string[] = [
        DeliveryOrderQuoteManagerStatus.WORKING,
        DeliveryOrderQuoteManagerStatus.PAID_BREAK,
        DeliveryOrderQuoteManagerStatus.UNPAID_BREAK,
        DeliveryOrderQuoteManagerStatus.NOT_WORKING,
    ];

    for (let i = 0; i < sortingOrder.length; i++) {
        orderSortingOrdering[sortingOrder[i]] = i;
    }

    return (
        orderSortingOrdering[managerA.timecardStatus] -
        orderSortingOrdering[managerB.timecardStatus]
    );
}

export function parseOrderMarkers(order: any): MapMarker[] {
    const markers: MapMarker[] = [];

    if (order?.driver?.latitude && order?.driver?.longitude) {
        markers.push({
            id: order.driver.id,
            latLng: {
                lat: order?.driver?.latitude,
                lng: order?.driver?.longitude,
            },
            iconMarkerUrl: `${environment.settings.url}assets/img/map-pin-driver.png`,
            scaledSize: {
                width: 40,
                height: 40,
            },
            zIndex: 50,
        });
    }

    if (order?.pickup?.latitude && order?.pickup?.longitude) {
        markers.push({
            id: order.pickup.id,
            latLng: {
                lat: order?.pickup?.latitude,
                lng: order?.pickup?.longitude,
            },
            iconMarkerUrl: `${environment.settings.url}assets/img/map-pin-restaurant.png`,
            scaledSize: {
                width: 40,
                height: 45,
            },
            zIndex: 40,
        });
    }

    if (order?.dropoff) {
        markers.push({
            id: order.dropoff.id,
            latLng: {
                lat: order?.dropoff?.latitude,
                lng: order?.dropoff?.longitude,
            },
            iconMarkerUrl: `${environment.settings.url}assets/img/map-pin-customer.png`,
            scaledSize: {
                width: 40,
                height: 45,
            },
            zIndex: 40,
        });
    }

    return markers;
}

export function parseLiveOrderMarkers(order: any, isLiveOrder: boolean): MapMarker[] {
    const markers: MapMarker[] = [];

    if (order?.courier?.location?.lat && order?.courier?.location?.lng) {

        markers.push({
            locationId: `${ order.id }`,
            id: `${ order.id }_${ DeliveryOrderMarkerType.COURIER }`,
            latLng: {
                lat: order.courier.location.lat,
                lng: order.courier.location.lng,
            },
            iconMarkerUrl: `${environment.settings.url}assets/img/map-pin-driver.svg`,
            scaledSize: {
                width: 48,
                height: 54,
            },
            zIndex: 40,
            tooltip: null,
            info: isLiveOrder ? getDriverPinInfoWindowContent(order) : null,
            type: DeliveryOrderMarkerType.COURIER
        });

        if (order.courier?.polyline && isLiveOrder) {
            markers.push({
                locationId: `${ order.id }`,
                latLng: null,
                id: `${ order.id }_${ DeliveryOrderMarkerType.NAVIGATION_PATH }`,
                polyline: order.courier.polyline,
                tooltip: null,
                info: null,
                type: DeliveryOrderMarkerType.NAVIGATION_PATH
            });
        }
    }

    if (order?.dropoff?.location?.lat && order?.dropoff?.location?.lng) {

        markers.push({
            id: `${ order.id }_${ DeliveryOrderMarkerType.DROPOFF }`,
            locationId: `${ order.id }`,
            latLng: {
                lat: order.dropoff.location.lat,
                lng: order.dropoff.location.lng,
            },
            iconMarkerUrl: `${environment.settings.url}assets/img/${ isLiveOrder ? 'map-pin-destination.png' : 'map-pin-customer.svg' }`,
            scaledSize: {
                width: isLiveOrder ? 32 : 52,
                height: isLiveOrder ? 32 : 61,
            },
            zIndex: 50,
            tooltip: `${ order.dropoff.name } - ${ order.dropoff.detailedAddress.address_line_1 }`,
            info: isLiveOrder ? getCustomerPinInfoWindowContent(order) : null,
            type: DeliveryOrderMarkerType.DROPOFF
        });
    }

    return markers;
}

export function parseLiveOrderStatus(status: DeliveryOrderStatus) {
    let result: string = status;

    switch(status) {
        case DeliveryOrderStatus.NEW:
            result = 'Orders.Status.New.PendingDispatch';
            break;

        case DeliveryOrderStatus.ACCEPTED:
            result = 'Orders.Status.AwaitingDriver';
            break;

        case DeliveryOrderStatus.SCHEDULED:
            result = 'Orders.Status.Scheduled';
            break;

        case DeliveryOrderStatus.PICKUP:
            result = 'Orders.Status.PickingUp';
            break;

        case DeliveryOrderStatus.DROPOFF:
            result = 'Orders.Status.OnTheWay';
            break;

        case DeliveryOrderStatus.DELIVERED:
            result = 'Orders.Status.Delivered';
            break;

        case DeliveryOrderStatus.CANCELLED:
            result = 'Orders.Status.Canceled';
            break;

        case DeliveryOrderStatus.RETURNED:
            result = 'Orders.Status.Returned';
            break;

        case DeliveryOrderStatus.ASSIGNED:
            result = 'Orders.Status.Assigned';
            break;

        case DeliveryOrderStatus.REJECTED:
            result = 'Order.Status.Rejected';
            break;

        case DeliveryOrderStatus.RETURNING:
            result = 'Orders.Status.Returning';
            break;
    }

    return result;
}

export function getRestaurantPinInfoWindowContent(order: DeliveryOrder) {
    let content = null;

    if ((order?.status === DeliveryOrderStatus.ASSIGNED || order?.status === DeliveryOrderStatus.PICKUP) &&
        order.livePickupAtEta?.isValid && order.livePickupAtEta.isValid()) {

        
        const template: string = `
            <div class="c-live-map__info-window">
                <div class="c-live-map__info-window-important mr-2">
                    :LABEL_0
                </div>
                <div class="c-live-map__info-window-mute">
                    :LABEL_1
                </div>
            </div>
        `;
        const label0 = { label: order.livePickupAtEta.diff(moment(), MOMENT_UNIT.MINUTES), params: {} }
        const label1 = { label: 'LiveView.Map.InfoWindow.MinsAway', params: {} }

        content = {
            template,
            labels: [label0, label1]
        }
    }

    return content;
}

export function getCustomerPinInfoWindowContent(order: DeliveryOrder) {
    let content = null;

    if ((order?.status === DeliveryOrderStatus.DROPOFF) &&
        order.liveDropoffAtEta?.isValid && order.liveDropoffAtEta.isValid()) {

        
        const template: string = `
            <div class="c-live-map__info-window">
                <div class="c-live-map__info-window-important mr-2">
                    :LABEL_0
                </div>
                <div class="c-live-map__info-window-mute">
                    :LABEL_1
                </div>
            </div>
        `;
        const label0 = { label: order.liveDropoffAtEta.diff(moment(), MOMENT_UNIT.MINUTES), params: {} }
        const label1 = { label: 'LiveView.Map.InfoWindow.MinsAway', params: {} }

        content = {
            template,
            labels: [label0, label1]
        }
    }

    return content;
}

export function getDriverPinInfoWindowContent(order: DeliveryOrder) {
    let content = null;


    if (order.status === DeliveryOrderStatus.ASSIGNED || order?.status === DeliveryOrderStatus.PICKUP || order?.status === DeliveryOrderStatus.DROPOFF) {

        const nameBreakDown: string[] = order.courier.name.split(' ');
        let driverName: string = nameBreakDown.length > 1 ? `${ nameBreakDown[0] } ${ nameBreakDown[1].slice(0, 1) }.`: nameBreakDown[0];
        const label0 = { label: `Order.ServiceProvider.${order.serviceProvider }`, params: {} }

        content = {
            template : `<div class="c-live-map__info-window">
                <span class="c-text--bold mr-2">
                    ${ driverName }
                </span>
                <span class="c-text--mute">
                    :LABEL_0
                </span>
            </div>`,
            labels: [label0]
        }
    }
    
    return content;
}
